import { InfoCircleOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from '@tanstack/react-router';
import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import MarkdownEditor, { README_EDITOR_OPTIONS } from '_/common/components/MarkdownEditor';
import { DEFAULT_README, SOURCE_STATUS_OPTIONS } from '_/constants/common';
import { useCollection } from '_/contexts/CollectionContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useSourceContextSafe } from '_/contexts/SourceContext';
import { useLoginPools } from '_/hooks/useLoginPools';
import {
  useCollectionSources,
  useSourceReadme,
  useCreateSource,
  useUpdateSource,
  useUpdateSourceReadme,
} from '_/hooks/useSources';
import { ISource } from '_/types/sources/source.types';
import createSuggestedSlug from '_/utils/createSuggestedSlug';
import { convertToTitleCase } from '_/utils/objectUtils';
import { checkIfNameExists } from '_/utils/validation/nameValidator';
import { validateSlug, invalidSlugErrorMessage, checkIfSlugExists } from '_/utils/validation/slugValidator';
import { Form, Input, Select, Button, Space, Alert, Divider, Tooltip } from 'antd';
import React, { useEffect, useState, ReactElement } from 'react';

const composeParams = (params?: string[]) =>
  (Array.isArray(params) && params.reduce((obj, key) => ({ ...obj, [key]: '' }), {})) || {};

function NewSource(): ReactElement {
  const [form] = Form.useForm();
  const params = useParams({ strict: false }) as Partial<{ sourceSlug?: string }>;
  const sourceSlug = params.sourceSlug || '';
  const history = useNavigate();

  const { selectedCollection, selectedCollectionSlug } = useCollection();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();
  const sourceContext = useSourceContextSafe();
  const selectedSource = sourceContext?.selectedSource || null;
  const selectedSourceSlug = sourceContext?.selectedSourceSlug || null;
  const { setBreadcrumbs } = useTopHeader();

  // Fetch data with React Query
  const { data: collectionSources = [] } = useCollectionSources(selectedCollection?.id);
  const { data: selectedSourceReadme = '' } = useSourceReadme(selectedSource?.id);
  const { data: loginPools = [] } = useLoginPools(
    selectedProject?.id,
    selectedOrgSlug || undefined,
    selectedProjectSlug || undefined,
  );

  // Mutations
  const { mutate: createSourceMutation, isPending: isCreating } = useCreateSource(
    selectedCollection?.id || '',
    selectedOrgSlug || '',
    selectedProjectSlug || '',
    selectedCollectionSlug || '',
    collectionSources,
  );
  const { mutate: updateSourceMutation, isPending: isUpdatingSource } = useUpdateSource(selectedCollection?.id);
  const { mutate: updateSourceReadmeMutation, isPending: isUpdatingReadme } = useUpdateSourceReadme(selectedSource?.id || '');

  const isSaving = isCreating || isUpdatingSource || isUpdatingReadme;

  const [saveSourceErrors, setSaveSourceErrors] = useState<any[]>([]);
  const [sourceLabels, setSourceLabels] = useState<string[]>([]);
  const [sourceParams, setSourceParams] = useState<{ [key: string]: string }>({});
  const [sourceReadme, setSourceReadme] = useState(DEFAULT_README);
  const [initialReadme, setInitialReadme] = useState(DEFAULT_README);
  const [slugIsSame, setSlugIsSame] = useState(true);

  const isEditMode = !!sourceSlug && sourceSlug !== '_new';

  // Initialize form when in edit mode
  useEffect(() => {
    if (selectedSource && isEditMode) {
      form.setFieldsValue({
        name: selectedSource.name,
        slug: selectedSource.slug,
        statusText: selectedSource.statusText || '',
        extractorId: selectedSource.extractorId || '',
        status: selectedSource.status || '',
        statusCodeFormula: selectedSource.statusCodeFormula || '',
        maxInputs: selectedSource.maxInputs || undefined,
        loginPoolId: selectedSource.loginPoolId || '',
      });
      setSourceLabels(selectedSource.labels || []);
      setSourceParams(selectedSource.parameters || {});
      if (selectedSourceReadme) {
        setSourceReadme(selectedSourceReadme);
        setInitialReadme(selectedSourceReadme);
      }
    } else if (!isEditMode) {
      // Initialize for new source
      form.setFieldsValue({
        name: '',
        slug: '',
        statusText: '',
        extractorId: '',
        statusCodeFormula: '',
        maxInputs: undefined,
        loginPoolId: '',
      });
      setSourceLabels([]);
      setSourceParams(composeParams(selectedCollection?.parameters));
      setSourceReadme(DEFAULT_README);
      setInitialReadme(DEFAULT_README);
    }
  }, [selectedSource, selectedSourceReadme, isEditMode, form, selectedCollection]);

  // Set up breadcrumbs
  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      {
        title: 'Collections',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
      },
    ];

    if (selectedCollection) {
      breadcrumbs.push({
        title: selectedCollection.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/home`,
      });
    }

    if (isEditMode && selectedSource) {
      breadcrumbs.push(
        {
          title: 'Sources',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources`,
        },
        {
          title: selectedSource.name || '',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/home`,
        },
        { title: 'Edit' },
      );
    } else {
      breadcrumbs.push(
        {
          title: 'Sources',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources`,
        },
        { title: 'New Source' },
      );
    }

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [
    setBreadcrumbs,
    selectedOrganization,
    selectedOrgSlug,
    selectedProject,
    selectedProjectSlug,
    selectedCollection,
    selectedCollectionSlug,
    selectedSource,
    selectedSourceSlug,
    isEditMode,
  ]);

  const onFinishFailed = () => {
    // Validation failed
  };

  const onFinish = async (values: any) => {
    setSaveSourceErrors([]);

    try {
      if (isEditMode && selectedSource) {
        // Build update object with only changed fields
        const updatedSource: Partial<ISource> = {};
        if (selectedSource.name !== values.name) updatedSource.name = values.name;
        if (selectedSource.statusText !== values.statusText) updatedSource.statusText = values.statusText;
        if (selectedSource.extractorId !== values.extractorId) updatedSource.extractorId = values.extractorId || null;
        if (JSON.stringify(selectedSource.labels) !== JSON.stringify(sourceLabels)) updatedSource.labels = sourceLabels;
        if (JSON.stringify(selectedSource.parameters) !== JSON.stringify(sourceParams)) updatedSource.parameters = sourceParams;
        if (selectedSource.statusCodeFormula !== values.statusCodeFormula)
          updatedSource.statusCodeFormula = values.statusCodeFormula;
        if (selectedSource.status !== values.status) updatedSource.status = values.status;
        if (selectedSource.maxInputs !== values.maxInputs)
          updatedSource.maxInputs = values.maxInputs ? parseInt(String(values.maxInputs)) : undefined;
        if (selectedSource.loginPoolId !== values.loginPoolId) updatedSource.loginPoolId = values.loginPoolId || null;

        const hasSourceUpdates = Object.keys(updatedSource).length > 0;
        const hasReadmeUpdates = initialReadme !== sourceReadme;

        // If both need to be updated, we need to track both completions
        if (hasSourceUpdates && hasReadmeUpdates) {
          let sourceSaved = false;
          let readmeSaved = false;

          const checkAndNavigate = () => {
            if (sourceSaved && readmeSaved) {
              void history({
                to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSource.slug}`,
              });
            }
          };

          updateSourceMutation(
            {
              sourceId: selectedSource.id,
              updates: updatedSource,
            },
            {
              onSuccess: () => {
                sourceSaved = true;
                checkAndNavigate();
              },
              onError: (error: any) => {
                setSaveSourceErrors(error.messages || [{ type: 'api', error: error.message || 'Unknown error' }]);
              },
            },
          );

          updateSourceReadmeMutation(sourceReadme, {
            onSuccess: () => {
              readmeSaved = true;
              checkAndNavigate();
            },
            onError: (error: any) => {
              setSaveSourceErrors(error.messages || [{ type: 'api', error: error.message || 'Unknown error' }]);
            },
          });
        } else if (hasSourceUpdates) {
          // Only update source
          updateSourceMutation(
            {
              sourceId: selectedSource.id,
              updates: updatedSource,
            },
            {
              onSuccess: () => {
                void history({
                  to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSource.slug}`,
                });
              },
              onError: (error: any) => {
                setSaveSourceErrors(error.messages || [{ type: 'api', error: error.message || 'Unknown error' }]);
              },
            },
          );
        } else if (hasReadmeUpdates) {
          // Only update readme
          updateSourceReadmeMutation(sourceReadme, {
            onSuccess: () => {
              void history({
                to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSource.slug}`,
              });
            },
            onError: (error: any) => {
              setSaveSourceErrors(error.messages || [{ type: 'api', error: error.message || 'Unknown error' }]);
            },
          });
        }
      } else {
        // Create new source
        // Client-side validation for uniqueness
        if (checkIfNameExists(collectionSources, values.name)) {
          setSaveSourceErrors([
            {
              type: 'name',
              error: `A source with the name "${values.name}" already exists`,
            },
          ]);
          return;
        }

        if (checkIfSlugExists(collectionSources, values.slug)) {
          setSaveSourceErrors([
            {
              type: 'slug',
              error: `A source with the id/slug "${values.slug}" already exists`,
            },
          ]);
          return;
        }

        const source = {
          name: values.name,
          slug: values.slug,
          statusText: values.statusText || '',
          extractorId: values.extractorId || null,
          labels: sourceLabels || [],
          parameters: sourceParams,
          status: '',
          statusCodeFormula: values.statusCodeFormula || '',
          maxInputs: values.maxInputs ? parseInt(String(values.maxInputs)) : undefined,
          loginPoolId: values.loginPoolId || null,
        };

        createSourceMutation(
          { source: source, sourceReadme: sourceReadme },
          {
            onError: (error: any) => {
              setSaveSourceErrors(error.messages || [{ type: 'api', error: error.message || 'Unknown error' }]);
            },
          },
        );
      }
    } catch (error: any) {
      console.error('Error saving source:', error);
      setSaveSourceErrors(error.messages || [{ type: 'api', error: error.message || 'Unknown error' }]);
    }
  };

  const onCancel = () => {
    if (isEditMode) {
      void history({
        to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSource?.slug}`,
      });
    } else {
      void history({
        to: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}`,
      });
    }
  };

  const onReadmeChange = (value: string) => {
    setSourceReadme(value);
  };

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (slugIsSame && !isEditMode) {
      form.setFieldsValue({
        name: name,
        slug: createSuggestedSlug(name),
      });
    }
  };

  const onSlugChange = () => {
    if (!isEditMode) {
      const name = form.getFieldValue('name');
      const slug = form.getFieldValue('slug');
      setSlugIsSame(slug === createSuggestedSlug(name));
    }
  };

  const onSourceParamsChange = (paramName: string, value: string) => {
    setSourceParams({
      ...sourceParams,
      [paramName]: value,
    });
  };

  // Filter errors by type
  const nameErrors = saveSourceErrors.filter((e: any) => e.type === 'name');
  const slugErrors = saveSourceErrors.filter((e: any) => e.type === 'slug');
  const apiErrors = saveSourceErrors.filter((e: any) => e.type === 'api');

  return (
    <div>
      {apiErrors.length > 0 && (
        <Alert
          title="Error"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {apiErrors.map((error: any, idx: number) => (
                <li key={idx}>{error.error}</li>
              ))}
            </ul>
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        initialValues={{
          name: '',
          slug: '',
          statusText: '',
          extractorId: '',
          statusCodeFormula: '',
          maxInputs: undefined,
          loginPoolId: '',
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please input the source name!' }]}
          validateStatus={nameErrors.length > 0 ? 'error' : undefined}
          help={nameErrors.length > 0 ? nameErrors[0].error : undefined}
        >
          <Input placeholder="Source name" onChange={onNameChange} />
        </Form.Item>

        <Form.Item
          label="Slug / ID - CANNOT BE CHANGED AFTER CREATION"
          name="slug"
          rules={[
            { required: true, message: 'Please input the source slug!' },
            {
              validator: function (_, value) {
                if (!value) return Promise.resolve();
                if (!validateSlug(value)) {
                  return Promise.reject(new Error(invalidSlugErrorMessage));
                }
                return Promise.resolve();
              },
            },
          ]}
          validateStatus={slugErrors.length > 0 ? 'error' : undefined}
          help={slugErrors.length > 0 ? slugErrors[0].error : undefined}
        >
          <Input placeholder="source-slug" disabled={isEditMode} onChange={onSlugChange} />
        </Form.Item>

        {isEditMode && (
          <Form.Item label="Status" name="status">
            <Select placeholder="Select Status">
              {SOURCE_STATUS_OPTIONS.map((option: string) => (
                <Select.Option value={option} key={option}>
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item label="Status Text" name="statusText">
          <Input placeholder="Status text" />
        </Form.Item>

        <Form.Item label="Extractor ID" name="extractorId">
          <Input placeholder="Extractor ID" />
        </Form.Item>

        <Form.Item label="Login Pool" name="loginPoolId">
          <Select placeholder="No Login Pool" allowClear>
            {loginPools.map(({ id, name }) => (
              <Select.Option value={id} key={id}>
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Maximum Allowed Inputs" name="maxInputs">
          <Input type="number" placeholder="No limit" />
        </Form.Item>

        <Form.Item label="Labels">
          <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder="Type and press Enter to add labels"
            value={sourceLabels}
            onChange={setSourceLabels}
            tokenSeparators={[',']}
          />
        </Form.Item>

        {/* Source Parameters */}
        {selectedCollection && Array.isArray(selectedCollection.parameters) && selectedCollection.parameters.length > 0 && (
          <>
            <Divider />
            <h4 style={{ fontSize: 14, marginBottom: 16 }}>Parameters</h4>
            {selectedCollection.parameters.map((param) => (
              <Form.Item label={convertToTitleCase(param)} key={param}>
                <Input
                  placeholder={convertToTitleCase(param)}
                  value={sourceParams[param] || ''}
                  onChange={(e) => onSourceParamsChange(param, e.target.value)}
                />
              </Form.Item>
            ))}
            <Divider />
          </>
        )}

        <Form.Item
          label={
            <span>
              Status Code Formula{' '}
              <Tooltip title="Falsy value means no change, -1 means blocked, otherwise return a number. Supports formula.js functions, PARAM('name') for source parameters, INPUT('name') for input values, and page row columns as variables.">
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            </span>
          }
          name="statusCodeFormula"
        >
          <Input placeholder="Status code formula" />
        </Form.Item>

        <div style={{ marginBottom: 25, marginTop: 25, position: 'relative' }}>
          <MarkdownEditor editorOptions={README_EDITOR_OPTIONS} onChange={onReadmeChange} value={sourceReadme} />
          {!sourceReadme && <div style={{ color: '#ff4d4f', fontSize: 14, marginTop: 4 }}>README is required</div>}
        </div>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={isSaving}>
              Save
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}

export default NewSource;
